import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { persistUsdaCheckoutCapability } from '../utils/usdaCheckoutCapability';
import { Lock, CreditCard, Loader2, CheckCircle, XCircle, Home, Bitcoin, Sun, Moon, WalletCards, Globe2, ChevronDown, Shield } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const LOGO_URL = "/holapay-mascot-logo.png";
const MASCOT_URL = "/holapay-cat.png";

export default function PublicPaymentPage() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();
  const { currentLanguage, changeLanguage, languages, t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [processingGatewayId, setProcessingGatewayId] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paidAmount, setPaidAmount] = useState(null); // Store the actual paid amount
  const [paidCurrency, setPaidCurrency] = useState(null); // Store the currency
  const [prefillTriggered, setPrefillTriggered] = useState(false);

  useEffect(() => {
    fetchPaymentLink();
    
    // Check if returning from payment
    const urlParams = new URLSearchParams(window.location.search);
    const orderId = urlParams.get('token');
    if (orderId) {
      capturePayment(orderId);
    }
  }, [linkId]);

  useEffect(() => {
    if (!data || prefillTriggered) return;
    const urlParams = new URLSearchParams(window.location.search);
    const amountParam = urlParams.get('amount');
    const orderIdParam = urlParams.get('order_id') || urlParams.get('orderId') || urlParams.get('orderID');
    const hasGatewayStatus = urlParams.get('nexi_status') || urlParams.get('payplug_status') || urlParams.get('crypto_status') || urlParams.get('gateway') || urlParams.get('status');
    if (!amountParam || !orderIdParam || hasGatewayStatus) return;

    if (!data?.merchant?.prefill_enabled) {
      if (!data?.payment_link?.amount) {
        setCustomAmount(amountParam);
      }
      return;
    }

    const prefillKey = `prefill_${linkId}_${amountParam}_${orderIdParam}`;
    if (sessionStorage.getItem(prefillKey)) return;
    sessionStorage.setItem(prefillKey, '1');

    const runPrefill = async () => {
      setPrefillTriggered(true);
      try {
        const response = await axios.post(`${API_URL}/api/public/payment-link/${linkId}/prefill`, {
          amount: amountParam,
          order_id: orderIdParam
        });
        const redirectUrl = response.data?.relative_url || response.data?.redirect_url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      } catch (error) {
        console.error('Prefill error:', error);
        toast.error('Errore nella precompilazione del pagamento');
        if (!data?.payment_link?.amount) {
          setCustomAmount(amountParam);
        }
      }
    };

    runPrefill();
  }, [data, linkId, prefillTriggered]);

  const fetchPaymentLink = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/public/payment-link/${linkId}`);
      setData(response.data);
      // Save return URL for the result page "Torna al Negozio" button
      // Priority: success_redirect > fail_redirect > metadata.site_url
      const pl = response.data.payment_link;
      const returnUrl = pl?.success_redirect || pl?.fail_redirect || pl?.metadata?.site_url;
      if (returnUrl) {
        sessionStorage.setItem('alphabit_return_url', returnUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Link di pagamento non trovato');
    } finally {
      setLoading(false);
    }
  };

  const capturePayment = async (orderId) => {
    setProcessing(true);
    try {
      const response = await axios.post(`${API_URL}/api/payments/capture-order/${orderId}`);
      if (response.data.status === 'success') {
        setPaymentStatus('success');
        // Save the paid amount from the response
        if (response.data.amount) {
          setPaidAmount(response.data.amount);
          setPaidCurrency(response.data.currency || 'EUR');
        }
        toast.success('Pagamento completato!');
        // Refresh payment link data to show updated status
        fetchPaymentLink();
      } else {
        setPaymentStatus('failed');
        toast.error('Pagamento fallito');
      }
    } catch (error) {
      console.error('Capture error:', error);
      
      // Check if the payment link is already paid (by checking status)
      try {
        const linkResponse = await axios.get(`${API_URL}/api/public/payment-link/${linkId}`);
        if (linkResponse.data.payment_link.status === 'paid') {
          // Payment was already completed successfully
          setPaymentStatus('success');
          toast.success('Pagamento completato!');
          setData(linkResponse.data);
          return;
        }
      } catch (e) {
        // Ignore errors from status check
      }
      
      setPaymentStatus('failed');
      toast.error('Errore nella conferma del pagamento');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async (
    selectedGateway = null,
    selectedGatewayId = null,
    selectedProviderId = null,
    selectedPaymentMethod = null
  ) => {
    const amount = data.payment_link.amount || parseFloat(customAmount);
    if (!amount || amount <= 0) {
      toast.error('Inserisci un importo valido');
      return;
    }

    setProcessing(true);
    const gateway = selectedGateway || data.payment_link.gateway || data.merchant?.gateway || null;
    if (!gateway) {
      setProcessing(false);
      toast.error('Nessun gateway di pagamento disponibile');
      return;
    }
    const gatewayId = selectedGatewayId ?? data.payment_link.gateway_id ?? null;
    const processingKey = `${gatewayId ?? gateway}:${selectedPaymentMethod ?? selectedProviderId ?? ''}`;
    setProcessingGatewayId(processingKey);
    try {
      // Use selected gateway or default
      
      // Select the correct API endpoint based on gateway
      let apiEndpoint = `${API_URL}/api/payments/create-order`;
      if (gateway === 'viva_wallet') {
        apiEndpoint = `${API_URL}/api/payments/viva/create-order`;
      } else if (gateway === 'roxpay') {
        apiEndpoint = `${API_URL}/api/payments/roxpay/create-order`;
      } else if (gateway === 'crypto') {
        apiEndpoint = `${API_URL}/api/payments/crypto/create-order`;
      } else if (gateway === 'nexi') {
        apiEndpoint = `${API_URL}/api/payments/nexi/create-order`;
      } else if (gateway === 'payplug') {
        apiEndpoint = `${API_URL}/api/payments/payplug/create-order`;
      } else if (gateway === 'sumup') {
        apiEndpoint = `${API_URL}/api/payments/sumup/create-order`;
      } else if (gateway === 'mollie') {
        apiEndpoint = `${API_URL}/api/payments/mollie/create-order`;
      } else if (gateway === 'silkpay') {
        apiEndpoint = `${API_URL}/api/payments/silkpay/create-order`;
      } else if (gateway === 'usda_pay') {
        apiEndpoint = `${API_URL}/api/payments/usda/create-order`;
      }
      
      // Build params - include gateway_id if provided for multi-gateway support
      const params = {
        payment_link_id: linkId,
        custom_amount: data.payment_link.amount ? null : amount
      };
      
      // CRITICAL: Pass gateway_id to backend for multi-gateway
       if (gatewayId != null) {
        params.selected_gateway_id = gatewayId;
      }
       // RoxPay can expose several catalog methods under one configured gateway.
       // Keep this parameter exclusive to RoxPay so all other gateways retain
       // their existing request contract.
       if (gateway === 'roxpay' && selectedProviderId != null) {
         params.selected_provider_id = selectedProviderId;
       }
       if (gateway === 'roxpay' && selectedPaymentMethod != null) {
         params.selected_payment_method = selectedPaymentMethod;
       }
      
      const response = await axios.post(apiEndpoint, null, { params });

      // Handle different gateway responses
      if (response.data.approval_url) {
        // PayPal
        window.location.href = response.data.approval_url;
      } else if (response.data.hosted_checkout_url) {
        // SumUp Hosted Checkout
        window.location.href = response.data.hosted_checkout_url;
      } else if (response.data.checkout_url) {
        // Viva Wallet / Rox Pay - Save order info to localStorage for confirmation after redirect
        if (gateway === 'viva_wallet' && response.data.order_code) {
          localStorage.setItem('viva_pending_order', JSON.stringify({
            order_code: response.data.order_code,
            transaction_id: response.data.transaction_id,
            payment_link_id: linkId,
            timestamp: Date.now()
          }));
        }
        window.location.href = response.data.checkout_url;
      } else if (response.data.payment_url) {
        // Rox Pay alternative
        window.location.href = response.data.payment_url;
      } else if (gateway === 'usda_pay' && response.data.qr_code && response.data.order_id) {
        // USDA Pay - navigate to inline checkout
        const capability = response.data.capability;
        if (!capability) {
          toast.error('Impossibile proteggere il checkout USDA. Riprova.');
          setProcessing(false);
          setProcessingGatewayId(null);
          return;
        }
        if (!persistUsdaCheckoutCapability(response.data.order_id, capability)) {
          toast.error('Impossibile avviare il checkout protetto. Riprova.');
          setProcessing(false);
          setProcessingGatewayId(null);
          return;
        }
        navigate(`/pay/usda/${response.data.order_id}`);
      } else {
        toast.error('Errore nella creazione del pagamento');
        setProcessing(false);
        setProcessingGatewayId(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel pagamento');
      setProcessing(false);
      setProcessingGatewayId(null);
    }
  };

  const formatCurrency = (amount, currency) => {
    const intlLocales = {
      it: 'it-IT',
      en: 'en-GB',
      fr: 'fr-FR',
      de: 'de-DE',
      es: 'es-ES',
      ar: 'ar-SA',
      ru: 'ru-RU'
    };
    return new Intl.NumberFormat(intlLocales[currentLanguage] || 'it-IT', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount || 0);
  };

  const renderCheckoutFrame = (content, frameClass = '') => (
    <div className={`hola-public-page hola-checkout-page ${isDark ? 'is-dark' : ''} ${frameClass}`}>
      <div className="hola-checkout-sun hola-checkout-sun-one" aria-hidden="true" />
      <div className="hola-checkout-sun hola-checkout-sun-two" aria-hidden="true" />
      <div className="hola-checkout-container">
        <header className="hola-checkout-header">
          <button
            type="button"
            className="hola-checkout-brand"
            onClick={() => navigate('/')}
            aria-label="Torna alla home Hola Pay"
          >
            <img src={LOGO_URL} alt="Hola Pay" />
          </button>
          <div className="hola-checkout-tools">
            <label className="hola-checkout-language">
              <Globe2 aria-hidden="true" />
              <span className="sr-only">{t('common.selectLanguage')}</span>
              <select
                value={currentLanguage}
                onChange={(event) => changeLanguage(event.target.value)}
                aria-label={t('common.selectLanguage')}
                data-testid="checkout-language-selector"
              >
                {languages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.code.toUpperCase()}
                  </option>
                ))}
              </select>
              <ChevronDown aria-hidden="true" />
            </label>
            <button
              type="button"
              onClick={toggleTheme}
              className="hola-checkout-theme"
              data-testid="theme-toggle"
              aria-label={isDark ? 'Passa alla modalità chiara' : 'Passa alla modalità scura'}
            >
              {isDark ? <Sun aria-hidden="true" /> : <Moon aria-hidden="true" />}
            </button>
          </div>
        </header>
        <main className="hola-checkout-main">{content}</main>
        <footer className="hola-checkout-footer">
          <span><Lock aria-hidden="true" /> Pagamento protetto</span>
          <span><CheckCircle aria-hidden="true" /> Dati sempre privati</span>
          <span>Hola Pay · Secure checkout</span>
        </footer>
      </div>
    </div>
  );

  if (loading) {
    return renderCheckoutFrame(
      <div className="hola-checkout-state">
        <Loader2 className="hola-checkout-spinner animate-spin" aria-hidden="true" />
        <p>Prepariamo il tuo pagamento...</p>
      </div>,
      'hola-checkout-loading'
    );
  }

  if (!data) {
    return renderCheckoutFrame(
      <section className="hola-checkout-state hola-checkout-state-card">
        <img src={MASCOT_URL} alt="" className="hola-state-mascot" />
        <XCircle className="hola-state-icon hola-state-icon-error" aria-hidden="true" />
        <h1>{t('payment.invalidLink')}</h1>
        <p>{t('payment.invalidLinkMessage')}</p>
      </section>,
      'hola-checkout-centered'
    );
  }

  const { payment_link, merchant, available_gateways } = data;
  const linkTitle = payment_link.title || payment_link.description || '';
  const branding = merchant?.branding || {};
  const primaryColor = branding.primary_color || '#F5D547';
  const buttonColor = branding.button_color || '#23282B';
  
  // Get gateway name for display
  const gateway = payment_link.gateway || merchant?.gateway || null;
  const gatewayNames = {
    paypal: 'PayPal',
    viva_wallet: 'Viva Wallet',
    roxpay: 'Rox Pay',
    stripe: 'Stripe',
    crypto: 'Crypto',
    nexi: 'NEXI XPay',
    payplug: 'PayPlug',
    sumup: 'SumUp',
    mollie: 'Mollie',
    silkpay: 'SilkPay',
    bank_transfer: 'Bonifico Bancario'
  };
  const gatewayDisplayName = gatewayNames[gateway] || gateway || 'Metodo di pagamento';
  
  // Gateway logos - real logos
  const gatewayLogos = {
    paypal: '/payment-brands/paypal-card-logo.png',
    viva_wallet: 'https://www.vivawallet.com/assets/images/logo.svg',
    roxpay: null, // Will use fallback
    stripe: 'https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg',
    crypto: 'https://upload.wikimedia.org/wikipedia/commons/4/46/Bitcoin.svg',
    nexi: 'https://www.nexi.it/content/dam/nexi/loghi/logo-nexi.svg',
    payplug: null, // Will use fallback
    sumup: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAgNDAiPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iNDAiIGZpbGw9IiMxQTFGNzEiIHJ4PSI0Ii8+PHRleHQgeD0iNjAiIHk9IjI2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+U3VtVXA8L3RleHQ+PC9zdmc+',
    mollie: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAxMjAgNDAiPjxyZWN0IHdpZHRoPSIxMjAiIGhlaWdodD0iNDAiIGZpbGw9IiMwMDBGMjIiIHJ4PSI0Ii8+PHRleHQgeD0iNjAiIHk9IjI2IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTYiIGZvbnQtd2VpZ2h0PSJib2xkIiBmaWxsPSJ3aGl0ZSIgdGV4dC1hbmNob3I9Im1pZGRsZSI+bW9sbGllPC90ZXh0Pjwvc3ZnPg==',
    bank_transfer: null // Will use fallback
  };
  
  // A RoxPay gateway is a catalog of payment methods, so expose each method
  // as its own choice while keeping the configured gateway id intact.
  const paymentChoices = (available_gateways || []).flatMap((gw) => {
    if (
      gw.name === 'roxpay'
      && gw.payment_methods_configured
      && (!Array.isArray(gw.payment_methods) || gw.payment_methods.length === 0)
    ) {
      return [];
    }
    if (gw.name !== 'roxpay' || !Array.isArray(gw.payment_methods) || gw.payment_methods.length === 0) {
      return [{
        key: `${gw.id}-${gw.name}`,
        gatewayName: gw.name,
        gatewayId: gw.id,
        providerId: null,
        methodKey: null,
        label: gw.name === 'usda_pay' ? 'USDA Pay' : (gatewayNames[gw.name] || gw.name),
        kind: null,
        description: null
      }];
    }

    return gw.payment_methods.map((method, index) => ({
      key: `${gw.id}-${method.method_key ?? method.provider_id ?? index}`,
      gatewayName: gw.name,
      gatewayId: gw.id,
      providerId: method.provider_id,
      methodKey: method.method_key || null,
      label: method.kind === 'card'
        ? 'Carta di Credito/Debito'
        : (method.label || 'Metodo di pagamento'),
      kind: method.kind || 'generic',
      description: method.description || null
    }));
  });
  const paymentChoiceOrder = {
    card: 1,
    apple_pay: 2,
    google_pay: 3,
    wallet: 4,
    bank: 5,
    klarna: 6
  };
  const orderedPaymentChoices = [...paymentChoices].sort((a, b) => {
    const aOrder = a.gatewayName === 'usda_pay' ? 0 : (a.gatewayName === 'roxpay' ? (paymentChoiceOrder[a.kind] ?? 10) : 20);
    const bOrder = b.gatewayName === 'usda_pay' ? 0 : (b.gatewayName === 'roxpay' ? (paymentChoiceOrder[b.kind] ?? 10) : 20);
    return aOrder - bOrder;
  });
  const singleChoice = paymentChoices.length === 1 ? paymentChoices[0] : null;
  const showPaymentChoices = paymentChoices.length > 1
    || (
      singleChoice?.gatewayName === 'roxpay'
      && (singleChoice?.providerId != null || singleChoice?.methodKey != null)
    );

  // Payment completed screen
  if (paymentStatus === 'success') {
    // Use paidAmount if available (from capture response), otherwise fallback to payment_link amount or customAmount
    const displayAmount = paidAmount || payment_link.amount || parseFloat(customAmount) || 0;
    const displayCurrency = paidCurrency || payment_link.currency || 'EUR';
    
    return renderCheckoutFrame(
      <section className="hola-checkout-state hola-checkout-state-card">
        <div className="hola-state-mascot-wrap">
          <img src={MASCOT_URL} alt="Hola Pay Cat festeggia il pagamento" className="hola-state-mascot" />
          <span className="hola-state-sticker">YAY!</span>
        </div>
        <div className="hola-state-icon hola-state-icon-success"><CheckCircle aria-hidden="true" /></div>
        <p className="hola-checkout-kicker">Pagamento completato</p>
        <h1>{t('payment.paymentCompleted')}</h1>
        <p>{t('payment.thankYou')}</p>
        <strong className="hola-state-amount" style={{ color: primaryColor }}>
          {formatCurrency(displayAmount, displayCurrency)}
        </strong>
        <Button
          onClick={() => navigate('/')}
          className="hola-pay-cta"
          style={{ backgroundColor: buttonColor }}
        >
          <Home className="h-4 w-4" aria-hidden="true" />
          {t('auth.backToHome')}
        </Button>
      </section>,
      'hola-checkout-centered'
    );
  }

  // Payment failed screen
  if (paymentStatus === 'failed') {
    return renderCheckoutFrame(
      <section className="hola-checkout-state hola-checkout-state-card">
        <div className="hola-state-mascot-wrap">
          <img src={MASCOT_URL} alt="Hola Pay Cat controlla il pagamento" className="hola-state-mascot" />
          <span className="hola-state-sticker hola-state-sticker-worried">Ops!</span>
        </div>
        <div className="hola-state-icon hola-state-icon-error"><XCircle aria-hidden="true" /></div>
        <p className="hola-checkout-kicker">Controlliamo insieme</p>
        <h1>{t('payment.paymentFailed')}</h1>
        <p>{t('payment.errorOccurred')}</p>
        <Button
          onClick={() => setPaymentStatus(null)}
          className="hola-pay-cta"
          style={{ backgroundColor: buttonColor }}
        >
          {t('payment.retry')}
        </Button>
      </section>,
      'hola-checkout-centered'
    );
  }

  // Link expired or paid
  if (payment_link.status === 'expired') {
    return renderCheckoutFrame(
      <section className="hola-checkout-state hola-checkout-state-card">
        <div className="hola-state-mascot-wrap">
          <img src={MASCOT_URL} alt="Hola Pay Cat aspetta il prossimo pagamento" className="hola-state-mascot" />
          <span className="hola-state-sticker hola-state-sticker-worried">Attesa</span>
        </div>
        <div className="hola-state-icon hola-state-icon-warning"><XCircle aria-hidden="true" /></div>
        <p className="hola-checkout-kicker">Link non più attivo</p>
        <h1>{t('payment.linkExpired')}</h1>
        <p>{t('payment.linkExpiredMessage')}</p>
        <Button onClick={() => navigate('/')} variant="outline" className="hola-secondary-cta">
          <Home className="h-4 w-4" aria-hidden="true" />
          {t('auth.backToHome')}
        </Button>
      </section>,
      'hola-checkout-centered'
    );
  }

  if (payment_link.status === 'paid') {
    return renderCheckoutFrame(
      <section className="hola-checkout-state hola-checkout-state-card">
        <div className="hola-state-mascot-wrap">
          <img src={MASCOT_URL} alt="Hola Pay Cat custodisce la ricevuta" className="hola-state-mascot" />
          <span className="hola-state-sticker">OK!</span>
        </div>
        <div className="hola-state-icon hola-state-icon-success"><CheckCircle aria-hidden="true" /></div>
        <p className="hola-checkout-kicker">Ricevuta già registrata</p>
        <h1>{t('payment.alreadyPaid')}</h1>
        <p>{t('payment.alreadyPaidMessage')}</p>
        <Button onClick={() => navigate('/')} variant="outline" className="hola-secondary-cta">
          <Home className="h-4 w-4" aria-hidden="true" />
          {t('auth.backToHome')}
        </Button>
      </section>,
      'hola-checkout-centered'
    );
  }

  return (
    renderCheckoutFrame(
      <div className="hola-checkout-grid" data-testid="public-payment-page">
        <section className="hola-checkout-story" aria-labelledby="checkout-story-title">
          <p className="hola-checkout-kicker">Hola Pay · checkout</p>
          <h1 id="checkout-story-title">
            Al pagamento
            <span> vi penso io!</span>
          </h1>
          <p className="hola-checkout-intro">
            Un’esperienza chiara, sicura e tutta da vivere. Hola Pay Cat ti accompagna fino all’ultimo click.
          </p>
          <div className="hola-mascot-stage">
            <div className="hola-mascot-speech">Al pagamento<br /><strong>vi penso io!</strong></div>
            <div className="hola-checkout-illustration-card" aria-hidden="true">
              <span className="hola-illustration-chip" />
              <span className="hola-illustration-card-line hola-illustration-card-line-wide" />
              <span className="hola-illustration-card-line hola-illustration-card-line-short" />
            </div>
            <span className="hola-checkout-illustration-lock" aria-hidden="true"><Lock /></span>
            <img src={MASCOT_URL} alt="Hola Pay Cat saluta e accompagna il pagamento" className="hola-checkout-mascot" />
            <span className="hola-mascot-badge"><Shield aria-hidden="true" /> Protetto</span>
          </div>
          <div className="hola-checkout-trust">
            <span><Lock aria-hidden="true" /> Crittografia SSL</span>
            <span><CheckCircle aria-hidden="true" /> Nessuna condivisione dati</span>
          </div>
        </section>

        <section className="hola-payment-card" aria-labelledby="payment-card-title">
          <div className="hola-payment-card-top">
            <div>
              <p className="hola-checkout-kicker">Pagamento sicuro</p>
              <h2 id="payment-card-title">{linkTitle || 'Completa il tuo pagamento'}</h2>
            </div>
            <span className="hola-payment-lock"><Lock aria-hidden="true" /></span>
          </div>

          <div className="hola-payment-amount">
            <p>{t('payment.amountToPay')}</p>
            {payment_link.amount ? (
              <strong data-testid="checkout-amount" style={{ color: primaryColor }}>
                {formatCurrency(payment_link.amount, payment_link.currency)}
              </strong>
            ) : (
              <div className="hola-custom-amount">
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  placeholder={t('payment.enterAmount')}
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  data-testid="custom-amount-input"
                />
                <span>Valuta: {payment_link.currency}</span>
              </div>
            )}
          </div>

          {paymentChoices.length === 0 ? (
            <div role="status" data-testid="payment-methods-unavailable" className="hola-payment-unavailable">
              <XCircle aria-hidden="true" />
              <p>Metodo di pagamento temporaneamente non disponibile</p>
              <span>Riprova tra poco oppure contatta l’esercente.</span>
            </div>
          ) : showPaymentChoices ? (
            <div className="hola-methods">
              <div className="hola-methods-heading">
                <h3>{t('payment.chooseMethod')}</h3>
                <span>{orderedPaymentChoices.length} opzioni</span>
              </div>
              <div className={`hola-method-grid ${orderedPaymentChoices.length === 1 ? 'is-single' : ''}`}>
                {orderedPaymentChoices.map((choice) => {
                  const choiceKey = `${choice.gatewayId ?? choice.gatewayName}:${choice.methodKey ?? choice.providerId ?? ''}`;
                  const isChoiceProcessing = processing && processingGatewayId === choiceKey;
                  return (
                    <Button
                      key={choice.key}
                      onClick={() => handlePayment(choice.gatewayName, choice.gatewayId, choice.providerId, choice.methodKey)}
                      disabled={processing || (!payment_link.amount && !customAmount)}
                      variant="outline"
                      aria-label={`Paga con ${choice.label}`}
                      aria-pressed={processingGatewayId === choiceKey}
                      className={`hola-method-card ${choice.kind === 'card' ? 'is-featured' : ''} ${isChoiceProcessing ? 'is-processing' : ''}`}
                      data-testid={`pay-button-${choice.gatewayName}-${choice.methodKey ?? choice.providerId ?? choice.gatewayId}`}
                    >
                      {choice.kind === 'card' && !processing && (
                        <span className="hola-method-check"><CheckCircle aria-hidden="true" /></span>
                      )}
                      {isChoiceProcessing ? (
                        <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
                      ) : choice.kind === 'klarna' ? (
                        <img src="/payment-brands/klarna-logo-cropped.png" alt="Klarna" className="h-10 w-24 rounded-md object-contain" />
                      ) : choice.kind === 'bank' ? (
                        <img src="/payment-brands/instant-bank-transfer-transparent.png" alt="Bonifico istantaneo, oltre 170 banche" className="h-[58px] w-[142px] object-contain" />
                      ) : choice.kind === 'wallet' ? (
                        <div className="hola-method-content"><WalletCards aria-hidden="true" /><span>Wallet <b>RoxPay</b></span></div>
                      ) : choice.kind === 'google_pay' ? (
                        <span className="hola-method-brand hola-method-brand-google" aria-hidden="true">
                          <img src="/payment-brands/google-pay.svg" alt="" />
                        </span>
                      ) : choice.kind === 'apple_pay' ? (
                        <span className="hola-method-brand hola-method-brand-apple" aria-hidden="true">
                          <img src="/payment-brands/apple-pay.svg" alt="" />
                        </span>
                      ) : choice.kind === 'card' ? (
                        <div className="hola-method-content">
                          <span className="hola-card-mark"><b>VISA</b><i /><em /></span>
                          <span>{t('payment.cardPayment')}</span>
                        </div>
                      ) : choice.kind === 'generic' ? (
                        <div className="hola-method-content"><WalletCards aria-hidden="true" /><span>{choice.label}</span></div>
                      ) : choice.gatewayName === 'paypal' ? (
                        <img src={gatewayLogos.paypal} alt="PayPal" className="h-10 w-24 object-contain" />
                      ) : choice.gatewayName === 'crypto' ? (
                        <div className="hola-method-content"><Bitcoin aria-hidden="true" /><span>Crypto</span></div>
                      ) : choice.gatewayName === 'usda_pay' ? (
                        <div className="hola-method-content hola-method-content-usda">
                          <img src="/payment-brands/usda-official-logo.png" alt="" aria-hidden="true" />
                          <span>USDA Pay</span>
                        </div>
                      ) : (
                        <div className="hola-method-content"><CreditCard aria-hidden="true" /><span>{t('payment.cardPayment')}</span></div>
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>
          ) : (
            <Button
              onClick={() => singleChoice?.gatewayName === 'roxpay'
                ? handlePayment(singleChoice.gatewayName, singleChoice.gatewayId, singleChoice.providerId, singleChoice.methodKey)
                : handlePayment()}
              disabled={processing || (!payment_link.amount && !customAmount)}
              className="hola-pay-cta"
              style={{ backgroundColor: buttonColor }}
              data-testid="pay-button"
            >
              {processing ? (
                <><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> {t('payment.processing')}</>
              ) : (
                <>{singleChoice?.kind === 'wallet' ? <WalletCards className="h-5 w-5" aria-hidden="true" /> : <CreditCard className="h-5 w-5" aria-hidden="true" />} {singleChoice?.label || 'Paga con Hola Pay'}</>
              )}
            </Button>
          )}

          <div className="hola-payment-security">
            <Shield aria-hidden="true" />
            <div><strong>{t('branding.securePayment')}</strong><span>I tuoi dati restano privati.</span></div>
          </div>
          {branding.footer_text && <p className="hola-payment-footer-note">{branding.footer_text}</p>}
        </section>
      </div>
    )
  );
}
